import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios'
import router from '@/router/index'
import {messages} from './messages'
import {ElMessage} from "element-plus";
import {Page, Pageable} from "@/domain/page";
import {reactive} from "vue";

const URL: string = '/v1/api'

enum RequestEnums {
    TIMEOUT = 40000,
    OVERDUE = 600, // 登录失效
    FAIL = 999, // 请求失败
    SUCCESS = 200, // 请求成功
}

const config = {
    // 默认地址
    baseURL: URL as string,
    // 设置超时时间
    timeout: RequestEnums.TIMEOUT as number,
    // 跨域时候允许携带凭证
    withCredentials: true
}

export const downloadState = reactive({
    activeRequests: 0
});

export function trackDownload<T>(task: () => Promise<T> | T): Promise<T> {
    downloadState.activeRequests += 1;

    return Promise.resolve()
        .then(task)
        .finally(() => {
            downloadState.activeRequests = Math.max(0, downloadState.activeRequests - 1);
        });
}

function covertTime(params: object | undefined): object | undefined {
    if (!params) {
        return params
    }
    const clone = Object.assign({}, params);
    const rangeFields = ['applicationDate', 'date', 'serviceDate', 'leaveDate', 'inDate', 'outDate'];

    for (const key of rangeFields) {
        const value = (clone as any)[key];
        if (value instanceof Array && value.length === 2) {
            Reflect.set(clone, key, null)
            Reflect.set(clone, `${key as string}Start`, value[0])
            Reflect.set(clone, `${key as string}End`, value[1])
        }
    }
    return clone;
}

class RequestHttp {
    // 定义成员变量并指定类型
    service: AxiosInstance;

    public constructor(config: AxiosRequestConfig) {
        // 实例化axios
        this.service = axios.create(config);

        /**
         * 请求拦截器
         * 客户端发送请求 -> [请求拦截器] -> 服务器
         * token校验(JWT) : 接受服务器返回的token,存储到vuex/pinia/本地储存当中
         */
        this.service.interceptors.request.use((config: AxiosRequestConfig) => {
                return config
            },
            (error: AxiosError) => {
                return Promise.reject(error)
            }
        )

        /**
         * 响应拦截器
         * 服务器换返回信息 -> [拦截统一处理] -> 客户端JS获取到信息
         */
        this.service.interceptors.response.use(
            (response: AxiosResponse) => {
                return response
            },
            (error: AxiosError) => {
                const {response} = error;
                if (response?.status === 401) {
                    const currentRoute = router.currentRoute.value;
                    if (currentRoute.path === '/eLearning') {
                        router.push({name: 'eLearningLogin'});
                    } else if (!currentRoute.meta.nonAuth) {
                        router.push({name: 'login'});
                    }
                }
                if (response?.status === 404) {
                    return Promise.reject();
                } else {
                    if (response?.status === 400 && response?.data) {
                        const responseData = response.data;
                        const errCode = typeof responseData === 'object'
                            ? Reflect.get(responseData as Object, 'code')
                            : responseData;
                        const msg = messages[errCode];
                        ElMessage.error(msg ? msg : errCode)
                        return Promise.reject({userNotified: true, code: errCode, response: responseData});
                    }
                    return Promise.reject(response?.data);
                }
            }
        )
    }

    // 常用方法封装
    get<T>(url: string, params?: object): Promise<T> {
        return this.service.get(url, {params: covertTime(params)})
            .then(res => res.data)
    }

    getPage<T>(url: string, params?: Pageable): Promise<Array<T>> {
        return this.service.get<Page<T>>(url, {params: covertTime(params)})
            .then((response: AxiosResponse<Page<T>>) => {
                const page: Page<T> = response.data
                if (params) {
                    params.total = page.total
                }
                return page.data ? page.data : []
            })
    }

    download(url: string, fileName: string, params?: object, extension = 'xls'): Promise<void> {
        return trackDownload(() => this.service.get<ArrayBuffer>(url, {
            params: covertTime(params),
            headers: {
                Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            },
            responseType: "arraybuffer"
        }).then((data) => {
            const blob = new Blob([data.data])
            const downloadElement = document.createElement('a')
            const href = window.URL.createObjectURL(blob)
            downloadElement.href = href
            downloadElement.download = `${fileName}.${extension}`
            document.body.appendChild(downloadElement)
            downloadElement.click()
            document.body.removeChild(downloadElement)
            window.URL.revokeObjectURL(href)
        }))
    }

    downloadPdf(url: string, fileName: string, params?: object): Promise<void> {
        return trackDownload(() => this.service.get<ArrayBuffer>(url, {
            params: covertTime(params),
            headers: {
                'Content-Type': 'application/json',
            },
            responseType: "arraybuffer"
        }).then((data) => {
            const blob = new Blob([data.data], {type: 'application/pdf'});
            const downloadElement = document.createElement('a');
            const href = window.URL.createObjectURL(blob);
            downloadElement.href = href;
            downloadElement.download = fileName + '.pdf';
            document.body.appendChild(downloadElement);
            downloadElement.click();
            document.body.removeChild(downloadElement);
            window.URL.revokeObjectURL(href);
        }).catch(error => {
            console.error("Error downloading PDF:", error);
        }));
    }

    getPdfBlob(url: string, params?: object): Promise<Blob> {
        return this.service.get<ArrayBuffer>(url, {
            params: covertTime(params),
            headers: {
                Accept: 'application/pdf'
            },
            responseType: 'arraybuffer'
        }).then(response => new Blob([response.data], {type: 'application/pdf'}));
    }

    downloadCsv(url: string, fileName: string, params?: object): Promise<void> {
        return trackDownload(() => this.service.get<ArrayBuffer>(url, {
            params: covertTime(params),
            headers: {
                'Content-Type': 'application/json',
            },
            responseType: 'arraybuffer',
        }).then((response) => {
            const blob = new Blob([response.data], {type: 'text/csv'});

            const downloadElement = document.createElement('a');
            const href = window.URL.createObjectURL(blob);
            downloadElement.href = href;
            downloadElement.download = `${fileName}.csv`;
            document.body.appendChild(downloadElement);
            downloadElement.click();

            document.body.removeChild(downloadElement);
            window.URL.revokeObjectURL(href);
        }).catch((error) => {
            console.error('Error downloading CSV:', error);
        }));
    }

    downloadExcel(url: string, fileName: string, params?: object): Promise<void> {
        return trackDownload(() => this.service.get<ArrayBuffer>(url, {
            params: covertTime(params),
            headers: {
                Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            },
            responseType: "arraybuffer"
        }).then((data) => {
            const blob = new Blob([data.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            const downloadElement = document.createElement('a');
            const href = window.URL.createObjectURL(blob);

            // Set up download attributes
            downloadElement.href = href;
            downloadElement.download = `${fileName}.xlsx`;

            // Append and trigger the download
            document.body.appendChild(downloadElement);
            downloadElement.click();

            // Clean up
            document.body.removeChild(downloadElement);
            window.URL.revokeObjectURL(href);
        }).catch((error) => {
            console.error('Download failed', error);
        }));
    }

    downloadTxt(url: string, params?: object): Promise<void> {
        return trackDownload(() => this.service.get<ArrayBuffer>(url, {
            params: covertTime(params),
            headers: {
                'Content-Type': 'application/json',
            },
            responseType: "arraybuffer"
        }).then((data) => {
            const contentDisposition = data.headers["content-disposition"];
            let filename = "default.dtl";

            if (contentDisposition) {
                const matches = contentDisposition.match(/filename="?([^"]+)"?/);
                if (matches && matches.length > 1) {
                    filename = matches[1];
                }
            }
            const blob = new Blob([data.data], {type: "text/plain"});
            const downloadElement = document.createElement("a");
            const href = window.URL.createObjectURL(blob);
            downloadElement.href = href;
            downloadElement.download = filename;
            document.body.appendChild(downloadElement);
            downloadElement.click();
            document.body.removeChild(downloadElement);
            window.URL.revokeObjectURL(href);
        }).catch(error => {
            console.error("Error downloading DTL file:", error);
        }));
    }

    post<T>(url: string, data?: any, params?: object): Promise<T> {
        return this.service.post(url, data, params).then(res => res.data);
    }

    put<T>(url: string, params?: object): Promise<T> {
        return this.service.put(url, params);
    }

    patch<T>(url: string, params?: object): Promise<T> {
        return this.service.patch(url, params);
    }

    delete<T>(url: string, params?: object): Promise<T> {
        return this.service.delete(url, {params});
    }
}

export const httpClient = new RequestHttp(config);
